import _ChevronRight from "@mui/icons-material/ChevronRight";
import _Box from "@mui/material/Box";
import _Typography from "@mui/material/Typography";
import _Skeleton from "@mui/material/Skeleton";
import _Button2 from "@mui/material/Button";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useChallenge } from '../../../hooks';
import useEventRankingQuery from '../../../hooks/use-event-ranking-query';
import { GeogroupAvatar } from '../../geogroup-avatar';
import { medals } from '../medals';
import { leaderboardSizesMap } from '../sizes';
function sizeRange(size) {
  return size === 'small' ? {
    max: 50
  } : size === 'medium' ? {
    min: 51,
    max: 500
  } : {
    min: 501
  };
}
function Leaderboard({
  Button: _Button,
  ownGeogroup,
  event,
  onOwnCompanyClick,
  selectLeaderboardSize
}) {
  const {
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const size = ownGeogroup === null ? 'large' : ownGeogroup?.companySize;
  const {
    data: topResult,
    isError: topIsError
  } = useEventRankingQuery(size ? event.id : undefined, {
    page: 1,
    pageSize: 3,
    groupTypes: ['company'],
    size: size ? sizeRange(size) : undefined
  });
  const leaderboard = topResult?.items;
  const closeEnabled = !!(ownGeogroup && leaderboard && !leaderboard.find(({
    id
  }) => ownGeogroup.id === id));
  const {
    data: closeResult,
    isError: closeIsError
  } = useEventRankingQuery(closeEnabled && size ? event.id : undefined, {
    page: 1,
    pageSize: 1,
    groupTypes: ['company'],
    size: size ? sizeRange(size) : undefined,
    groupsIds: ownGeogroup ? [ownGeogroup.id] : undefined
  });
  const closeLeaderboard = !ownGeogroup || !leaderboard ? [] : closeEnabled ? closeResult?.items : [];
  useEffect(() => {
    if (topIsError || closeIsError) {
      enqueueSnackbar(t('commons.challenge_details.server_error_awards'), {
        variant: 'error'
      });
    }
  }, [topIsError, closeIsError]);
  if (leaderboard && leaderboard.length === 0) return /*#__PURE__*/React.createElement(React.Fragment, null);
  const Button = _Button || _Button2;
  return /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    border: "1px solid #D9E7FF",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: 3
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700
  }, size ? t(leaderboardSizesMap[size].titleKey) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 200
  })), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 1
  }, size && leaderboard ? leaderboard.map((item, index) => /*#__PURE__*/React.createElement(Item, {
    challengeType: event.challengeTemplate?.type,
    item: item,
    key: item.id,
    onOwnCompanyClick: onOwnCompanyClick,
    ownGeogroup: ownGeogroup,
    podiumIndex: index
  })) : [0, 1, 3].map(index => /*#__PURE__*/React.createElement(Item, {
    challengeType: event.challengeTemplate?.type,
    key: index,
    podiumIndex: index
  }))), closeLeaderboard && closeLeaderboard.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Box, {
    borderTop: "2px dashed #dde0e7"
  }), closeLeaderboard.map(item => /*#__PURE__*/React.createElement(Item, {
    challengeType: event.challengeTemplate?.type,
    item: item,
    key: item.id,
    onOwnCompanyClick: onOwnCompanyClick,
    ownGeogroup: ownGeogroup
  }))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    justifyContent: "center"
  }, /*#__PURE__*/React.createElement(Button, {
    color: "secondary",
    disabled: !size,
    endIcon: /*#__PURE__*/React.createElement(_ChevronRight, null),
    onClick: () => size && selectLeaderboardSize(size),
    size: "medium",
    variant: "contained"
  }, t('commons.challenges.leaderboard.actions.see_leaderboard'))));
}
function Item({
  Button: _Button,
  ownGeogroup,
  challengeType,
  podiumIndex,
  item,
  onOwnCompanyClick
}) {
  const {
    getProgressValueWithUnit
  } = useChallenge();
  const Button = _Button || _Button2;
  const medal = podiumIndex !== undefined && medals[podiumIndex] || null;
  return /*#__PURE__*/React.createElement(_Box, _extends({
    alignItems: "center",
    bgcolor: medal?.color,
    borderRadius: 2,
    display: "flex",
    gap: 1,
    padding: 2,
    sx: {
      justifyContent: 'flex-start',
      textAlign: 'left',
      textTransform: 'initial',
      color: 'initial'
    }
  }, onOwnCompanyClick && item && ownGeogroup && ownGeogroup.id === item.id ? {
    component: Button,
    onClick: onOwnCompanyClick
  } : {}), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexShrink: 0,
    height: 24,
    justifyContent: "center",
    width: 30
  }, medal ? /*#__PURE__*/React.createElement("img", {
    src: medal.icon,
    width: 24
  }) : /*#__PURE__*/React.createElement(_Typography, {
    align: "center",
    fontWeight: 700
  }, item ? Math.max(item.rank, item.leaderboardIndex) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 24
  }))), /*#__PURE__*/React.createElement(GeogroupAvatar, {
    geogroup: item,
    size: 48
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    sx: {
      overflowY: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    noWrap: true,
    fontWeight: 700,
    title: item?.title,
    variant: "body2"
  }, item?.title || /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 200
  })), /*#__PURE__*/React.createElement(_Typography, {
    variant: "caption"
  }, item ? getProgressValueWithUnit(challengeType || 'bttf', item.progressValue) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 100
  }))));
}
export default Leaderboard;